/// <amd-module name="Release/Client/Service/Release" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { ITfsPageService } from "Tfs/Platform/Page";

import { IReleaseRestClient } from "Release/Client/RestClient/Release";
import {
    ReleaseDefinition,
    Release,
    ReleaseStartMetadata,
    ReleaseDefinitionExpands,
    ReleaseDefinitionQueryOrder,
    ReleaseDefinitionEnvironmentTemplate,
    Deployment,
    DeploymentStatus,
    DeploymentOperationStatus,
    ReleaseQueryOrder,
    Folder,
    ReleaseStatus,
    ReleaseExpands,
    EnvironmentStatus,
    ReleaseUpdateMetadata,
    ReleaseApproval,
    ApprovalStatus,
    ApprovalType,
    ApprovalFilters,
    SingleReleaseExpands,
    ReleaseEnvironment,
    ReleaseEnvironmentExpands
} from "Release/Client/Contracts/Release";
import { IReleasesResult, IDeploymentsResult } from "Contracts/Extended";
import { IExtendedReleaseClient } from "RestClient/Extended";

export interface IReleaseService extends IVssService {

    /* Folders */
    createFolder(folderPath: string): Promise<Folder>;
    updateFolder(folder: Folder, path: string): Promise<Folder>;
    deleteFolder(folderPath: string): Promise<void>;

    /* Release definitions */
    getReleaseDefinition(id: number): Promise<ReleaseDefinition>;
    createReleaseDefinition(definition: ReleaseDefinition): Promise<ReleaseDefinition>;
    updateReleaseDefinition(definition: ReleaseDefinition): Promise<ReleaseDefinition>;
    deleteReleaseDefinition(id: number, comment: string, forceDelete: boolean | undefined): Promise<void>;
    undeleteReleaseDefinition(id: number, comment: string): Promise<ReleaseDefinition>;
    getReleaseDefinitions(
        searchText?: string,
        expand?: ReleaseDefinitionExpands,
        artifactType?: string,
        artifactSourceId?: string,
        top?: number,
        continuationToken?: string,
        queryOrder?: ReleaseDefinitionQueryOrder,
        path?: string,
        isExactNameMatch?: boolean,
        tagFilter?: string[],
        propertyFilters?: string[],
        definitionIdFilter?: string[],
        isDeleted?: boolean,
        searchTextContainsFolderName?: boolean): Promise<ReleaseDefinition[]>;

    /* Releases */
    createRelease(releaseStartMetadata: ReleaseStartMetadata): Promise<Release>;
    listReleases(definitionId: number,
        definitionEnvironmentId?: number,
        searchText?: string,
        statusFilter?: ReleaseStatus,
        branchFilter?: string,
        isDeleted?: boolean,
        tagFilter?: string[],
        createdBy?: string,
        continuationToken?: number,
        folderPath?: string,
        artifactTypeId?: string,
        sourceId?: string,
        artifactVersionId?: string,
        environmentStatusFilter?: number,
        minCreatedTime?: Date | undefined,
        maxCreatedTime?: Date | undefined,
        queryOrder?: ReleaseQueryOrder,
        top?: number,
        propertyFilters?: string[],
        releaseIdFilter?: number[]): Promise<IReleasesResult>;
    getRelease(releaseId: number,
        approvalFilters?: ApprovalFilters,
        propertyFilters?: string[],
        expand?: SingleReleaseExpands,
        topGateRecords?: number): Promise<Release>;
    updateRelease(releaseData: Release): Promise<Release>;
    patchRelease(releaseId: number, releaseUpdateData: ReleaseUpdateMetadata): Promise<Release>;
    undeleteRelease(releaseId: number, comment: string): Promise<void>;
    deleteRelease(releaseId: number, comment: string): Promise<void>;
    abandonRelease(release: Release, comment: string): Promise<Release>;
    retainRelease(releaseId: number, keepForever: boolean, comment: string): Promise<Release>;
    getReleaseEnvironment(releaseId: number, releaseEnvironmentId: number, expand: ReleaseEnvironmentExpands): Promise<ReleaseEnvironment>;

    /* Deployments*/
    getDeployments(definitionId?: number,
        definitionEnvironmentId?: number,
        createdBy?: string,
        minModifiedTime?: Date,
        maxModifiedTime?: Date,
        deploymentStatus?: DeploymentStatus,
        operationStatus?: DeploymentOperationStatus,
        latestAttemptsOnly?: boolean,
        queryOrder?: ReleaseQueryOrder,
        top?: number,
        continuationToken?: number,
        createdFor?: string,
        minStartedTime?: Date,
        maxStartedTime?: Date,
        sourceBranch?: string): Promise<IDeploymentsResult>;
    getLastDeployment(definitionId: number): Promise<Deployment | undefined>;

    /* Approvals */
    getApprovals(assignedToFilter?: string, statusFilter?: ApprovalStatus, releaseIdsFilter?: number[], typeFilter?: ApprovalType, top?: number, continuationToken?: number, queryOrder?: ReleaseQueryOrder, includeMyGroupApprovals?: boolean): Promise<ReleaseApproval[]>;

    /* Miscellaneous */
    getTags(): Promise<string[]>;
    getBranches(definitionId: number): Promise<string[]>;

    /* Templates */
    getDefinitionEnvironmentTemplate(templateId: string): Promise<ReleaseDefinitionEnvironmentTemplate>;
}

class ReleaseService extends VssService implements IReleaseService {
    public getReleaseDefinition(id: number): Promise<ReleaseDefinition> {
        const project = this._getProjectName();
        return this._getClient().getReleaseDefinition(project, id);
    }

    public createReleaseDefinition(definition: ReleaseDefinition): Promise<ReleaseDefinition> {
        const project = this._getProjectName();
        return this._getClient().createReleaseDefinition(definition, project);
    }

    public updateReleaseDefinition(definition: ReleaseDefinition): Promise<ReleaseDefinition> {
        const project = this._getProjectName();
        return this._getClient().updateReleaseDefinition(definition, project);
    }

    public deleteReleaseDefinition(id: number, comment: string, forceDelete: boolean = false): Promise<void> {
        const project = this._getProjectName();
        return this._getClient().deleteReleaseDefinition(project, id, comment, forceDelete);
    }

    public undeleteReleaseDefinition(id: number, comment: string): Promise<ReleaseDefinition> {
        const project = this._getProjectName();
        return this._getClient().undeleteReleaseDefinition({ comment: comment }, project, id);
    }

    public getRelease(releaseId: number, approvalFilters?: ApprovalFilters, propertyFilters?: string[], expand?: SingleReleaseExpands, topGateRecords?: number): Promise<Release> {
        const project = this._getProjectName();
        return this._getClient().getRelease(project, releaseId, approvalFilters, propertyFilters, expand, topGateRecords);
    }

    public createRelease(releaseStartMetadata: ReleaseStartMetadata): Promise<Release> {
        const project = this._getProjectName();
        return this._getClient().createRelease(releaseStartMetadata, project);
    }

    public updateRelease(releaseData: Release): Promise<Release> {
        const project = this._getProjectName();
        return this._getClient().updateRelease(releaseData, project, releaseData.id);
    }

    public patchRelease(releaseId: number, releaseUpdateData: ReleaseUpdateMetadata): Promise<Release> {
        const project = this._getProjectName();
        return this._getClient().updateReleaseResource(releaseUpdateData, project, releaseId);
    }

    public undeleteRelease(releaseId: number, comment: string): Promise<void> {
        const project = this._getProjectName();
        return this._getClient().undeleteRelease(project, releaseId, comment);
    }

    public deleteRelease(releaseId: number, comment: string): Promise<void> {
        const project = this._getProjectName();
        return this._getClient().deleteRelease(project, releaseId, comment);
    }

    public abandonRelease(release: Release, comment: string): Promise<Release> {
        const updateMetaData: ReleaseUpdateMetadata = {
            comment: comment,
            keepForever: release.keepForever,
            manualEnvironments: [],
            status: ReleaseStatus.Abandoned,
            name: ""
        };

        return this.patchRelease(release.id, updateMetaData);
    }

    public retainRelease(releaseId: number, keepForever: boolean, comment: string): Promise<Release> {
        const updateMetaData: ReleaseUpdateMetadata = {
            comment: comment,
            keepForever: keepForever,
            manualEnvironments: [],
            status: ReleaseStatus.Undefined,
            name: ""
        };

        return this.patchRelease(releaseId, updateMetaData);
    }

    public getTags(): Promise<string[]> {
        return this._getClient().getTags(this._getProjectName());
    }

    public getBranches(definitionId: number): Promise<string[]> {
        return this._getClient().getSourceBranches(this._getProjectName(), definitionId);
    }

    public getReleaseDefinitions(
        searchText?: string,
        expand?: ReleaseDefinitionExpands,
        artifactType?: string,
        artifactSourceId?: string,
        top?: number,
        continuationToken?: string,
        queryOrder?: ReleaseDefinitionQueryOrder,
        path?: string,
        isExactNameMatch?: boolean,
        tagFilter?: string[],
        propertyFilters?: string[],
        definitionIdFilter?: string[],
        isDeleted?: boolean,
        searchTextContainsFolderName?: boolean): Promise<ReleaseDefinition[]> {

        return this._getClient().getReleaseDefinitions(this._getProjectName(), searchText, expand, artifactType, artifactSourceId, top, continuationToken, queryOrder, path, isExactNameMatch, tagFilter, propertyFilters, definitionIdFilter, isDeleted, searchTextContainsFolderName);
    }

    public getDeployments(definitionId?: number,
        definitionEnvironmentId?: number,
        createdBy?: string,
        minModifiedTime?: Date,
        maxModifiedTime?: Date,
        deploymentStatus?: DeploymentStatus,
        operationStatus?: DeploymentOperationStatus,
        latestAttemptsOnly?: boolean,
        queryOrder?: ReleaseQueryOrder,
        top?: number,
        continuationToken?: number,
        createdFor?: string,
        minStartedTime?: Date,
        maxStartedTime?: Date,
        sourceBranch?: string): Promise<IDeploymentsResult> {

        return this.pageContext.getRestClient<IExtendedReleaseClient>("IExtendedReleaseClient").getDeploymentsWithContinuationToken(this._getProjectName(), definitionId, definitionEnvironmentId, createdBy, minModifiedTime, maxModifiedTime, deploymentStatus, operationStatus, latestAttemptsOnly, queryOrder, top, continuationToken, createdFor, minStartedTime, maxStartedTime, sourceBranch);
    }

    getApprovals(assignedToFilter?: string, statusFilter?: ApprovalStatus, releaseIdsFilter?: number[], typeFilter?: ApprovalType, top?: number, continuationToken?: number, queryOrder?: ReleaseQueryOrder, includeMyGroupApprovals?: boolean): Promise<ReleaseApproval[]> {
        return this._getClient().getApprovals(
            this._getProjectName(),
            assignedToFilter,
            statusFilter,
            releaseIdsFilter,
            typeFilter,
            top,
            continuationToken,
            queryOrder,
            includeMyGroupApprovals);
    }

    public getLastDeployment(defintionId: number): Promise<Deployment | undefined> {
        return this.getDeployments(defintionId, undefined, undefined, undefined, undefined, DeploymentStatus.All, DeploymentOperationStatus.All, undefined, ReleaseQueryOrder.Descending, 1).
            then((result: IDeploymentsResult) => result.deployments[0]);
    }

    public createFolder(folderPath: string): Promise<Folder> {
        let folderToCreate: Folder = {
            path: folderPath
        } as Folder;

        return this._getClient().createFolder(folderToCreate, this._getProjectName(), folderPath);
    }

    public updateFolder(folder: Folder, path: string): Promise<Folder> {
        return this._getClient().updateFolder(folder, this._getProjectName(), path);
    }

    public deleteFolder(folderPath: string): Promise<void> {
        return this._getClient().deleteFolder(this._getProjectName(), folderPath);
    }

    public listReleases(definitionId: number,
        definitionEnvironmentId: number = 0,
        searchText: string = "",
        statusFilter: ReleaseStatus = ReleaseStatus.Undefined,
        branchFilter: string = "",
        isDeleted: boolean = false,
        tagFilter: string[] = [],
        createdBy: string = "",
        continuationToken: number = 0,
        folderPath: string = "",
        artifactTypeId: string = "",
        sourceId: string = "",
        artifactVersionId: string = "",
        environmentStatusFilter: number = EnvironmentStatus.Undefined,
        minCreatedTime: Date | undefined = undefined,
        maxCreatedTime: Date | undefined = undefined,
        queryOrder: ReleaseQueryOrder = ReleaseQueryOrder.Descending,
        top: number = 25,
        propertyFilters: string[] = [],
        releaseIdFilter: number[] = []): Promise<IReleasesResult> {

        let releaseExpands: ReleaseExpands = ReleaseExpands.Environments | ReleaseExpands.Artifacts | ReleaseExpands.Approvals | ReleaseExpands.Tags | ReleaseExpands.ManualInterventions;

        return this.pageContext.getRestClient<IExtendedReleaseClient>("IExtendedReleaseClient").getReleasesWithToken(
            this._getProjectName(),
            definitionId,
            definitionEnvironmentId,
            searchText,
            createdBy,
            statusFilter,
            environmentStatusFilter,
            minCreatedTime,
            maxCreatedTime,
            queryOrder,
            top,
            continuationToken,
            folderPath,
            releaseExpands,
            artifactTypeId,
            sourceId,
            artifactVersionId,
            branchFilter,
            isDeleted,
            tagFilter,
            propertyFilters,
            releaseIdFilter
        );
    }

    public getDefinitionEnvironmentTemplate(templateId: string): Promise<ReleaseDefinitionEnvironmentTemplate> {
        const project = this._getProjectName();
        return this._getClient().getDefinitionEnvironmentTemplate(project, templateId);
    }

    public getReleaseEnvironment(releaseId: number, releaseEnvironmentId: number, expand: ReleaseEnvironmentExpands): Promise<ReleaseEnvironment> {
        const project = this._getProjectName();
        return this._getClient().getReleaseEnvironment(project, releaseId, releaseEnvironmentId, expand);
    }

    private _getProjectName(): string {
        const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            return tfsPageData.project.name;
        }

        throw new Error("Release rest client :: Project is not available in page data. Cannot make this network call without project information");
    }

    private _getClient(): IReleaseRestClient {
        return this.pageContext.getRestClient<IReleaseRestClient>("IReleaseRestClient");
    }
}

Services.add("IReleaseService", { serviceFactory: ReleaseService });
